import { createFileRoute, Link } from "@tanstack/react-router";
import useRequest from "@/lib/request/useRequest";
import { getColumnValues } from "@/lib/utils/table";
import {
    faArrowDownShortWide,
    faArrowUpShortWide,
    faDownload,
    faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Checkbox,
    Chip,
    Divider,
    Input,
    Select,
    SelectItem,
    Selection,
} from "@heroui/react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    Header,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldSummary } from "@/types/field";
import useLayout from "@/lib/layout/useLayout";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Status } from "@/types/enums";
import { isNumber, roundTo } from "@/lib/utils/numbers";

export const Route = createFileRoute("/dashboard/documentation/")({
    component: RouteComponent,
});

type HarvestSummary = FieldSummary & {
    has_harvest_operation: boolean;
    estimated_n_applied: number | null;
};

function RouteComponent() {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { setLayoutState } = useLayout();
    useEffect(() => {
        setLayoutState("full_screen");
    }, [setLayoutState]);

    const { t } = useTranslation("messages");
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(550);
    useEffect(() => {
        if (ref.current) {
            setHeight(ref.current.clientHeight);
        }
    }, []);

    const [selectedYear, setSelectedYear] = useState<Selection>(
        new Set(["2025"]),
    );

    const fieldSummaryQuery = useGetRequest<HarvestSummary[]>({
        url: "/fields/harvest_summary/",
        params: {
            year:
                selectedYear !== "all"
                    ? Array.from(selectedYear)[0]
                    : undefined,
        },
    });
    const [selectedFieldIds, setSelectedFieldIds] = useState<number[]>([]);

    const tableData = useMemo(() => {
        return (
            fieldSummaryQuery.data?.filter(
                (field) => field.status === Status.Active,
            ) ?? []
        );
    }, [fieldSummaryQuery.data]);

    const columns: ColumnDef<HarvestSummary>[] = useMemo(
        () => [
            {
                id: "selection",
                header: () => {
                    return (
                        <Checkbox
                            isSelected={
                                selectedFieldIds.length === tableData.length
                            }
                            onValueChange={(value) => {
                                setSelectedFieldIds(
                                    value
                                        ? tableData.map((field) => field.id)
                                        : [],
                                );
                            }}
                        />
                    );
                },
                accessorFn: (row) => row?.id ?? "",
                cell: ({ row }) => {
                    return (
                        <Checkbox
                            className="pl-4"
                            isSelected={selectedFieldIds.includes(
                                row.original.id,
                            )}
                            onValueChange={(value) => {
                                if (value) {
                                    setSelectedFieldIds((value) => [
                                        ...value,
                                        row.original.id,
                                    ]);
                                } else {
                                    setSelectedFieldIds((value) =>
                                        [...value].filter(
                                            (id) => id !== row.original.id,
                                        ),
                                    );
                                }
                            }}
                        ></Checkbox>
                    );
                },
                size: 50,
            },
            {
                id: "service_provider",
                header: "Service provider",
                accessorFn: (row) => row?.service_provider?.name ?? "",
                size: 300,
            },
            {
                id: "farm",
                header: "Farm",
                accessorFn: (row) => row?.grower?.name ?? "",
                size: 300,
            },
            {
                id: "field",
                header: "Field",
                accessorFn: (row) => row?.name ?? "",
                cell: ({ row }) => {
                    return (
                        <Link
                            to="/dashboard/fields/$fieldId"
                            params={{ fieldId: row.original.id.toString() }}
                            className="flex flex-col items-start justify-between w-full"
                        >
                            {row.original.name}
                        </Link>
                    );
                },
            },
            {
                id: "has_harvest_operation",
                header: "Has harvest operation for year",
                accessorFn: (row) =>
                    row?.has_harvest_operation ? "Yes" : "No",
                cell: ({ row }) => {
                    return (
                        <Chip
                            color={
                                row.original.has_harvest_operation
                                    ? "secondary"
                                    : "default"
                            }
                        >
                            {row.original.has_harvest_operation ? "Yes" : "No"}
                        </Chip>
                    );
                },
                size: 200,
            },
            {
                id: "estimated_n_applied",
                header: "Estimated N applied",
                accessorFn: (row) => row?.estimated_n_applied ?? "--",
                cell: ({ row }) => {
                    return (
                        <Chip
                            color={
                                row.original.estimated_n_applied
                                    ? "secondary"
                                    : "default"
                            }
                        >
                            {isNumber(row.original.estimated_n_applied) &&
                            row.original.estimated_n_applied !== null
                                ? roundTo(row.original.estimated_n_applied, 1)
                                : "--"}
                        </Chip>
                    );
                },
                size: 200,
            },
        ],
        [selectedFieldIds, tableData],
    );

    const [globalSearch, setGlobalSearch] = useState("");
    const [globalFilter, setGlobalFilter] = useState("");

    useEffect(() => {
        const debouncedUpdate = debounce(() => {
            setGlobalFilter(globalSearch);
        }, 200);

        debouncedUpdate();

        return () => debouncedUpdate.cancel();
    }, [globalSearch]);

    const table = useReactTable<HarvestSummary>({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            globalFilter,
        },
        initialState: {
            sorting: [{ id: "field", desc: false }],
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
    });
    const { rows } = table.getRowModel();

    const { rows: prefilteredRows } = table.getPreFilteredRowModel();

    const filterOptions = useMemo(
        () => ({
            service_provider: getColumnValues(
                "service_provider",
                prefilteredRows,
            ).map((service_provider) => {
                return (
                    <AutocompleteItem
                        key={service_provider}
                        textValue={
                            service_provider != "" ? service_provider : "CSP"
                        }
                    >
                        {service_provider}
                    </AutocompleteItem>
                );
            }),

            farm: getColumnValues("farm", prefilteredRows).map((farm) => {
                return (
                    <AutocompleteItem
                        key={farm}
                        textValue={farm != "" ? farm : "no farm"}
                    >
                        {farm}
                    </AutocompleteItem>
                );
            }),
        }),
        [prefilteredRows],
    );

    const downloadMutate = useMutateRequest<Blob>({
        errorMessage: t("error.failed_to_export"),
    });

    const handleExport = useCallback(() => {
        const year = Array.from(selectedYear)[0];
        downloadMutate.mutate(
            {
                url: "/fields/harvest_report/",
                method: "POST",
                data: {
                    year: year,
                    field_ids: selectedFieldIds,
                },
                responseType: "blob",
            },
            {
                onSuccess: (response) => {
                    const blob = new Blob([response], {
                        type: "application/zip",
                    });
                    const selectedFields = tableData.filter((field) =>
                        selectedFieldIds.includes(field.id),
                    );
                    const uniqueGrowers = Array.from(
                        new Set(
                            selectedFields.map(
                                (field) => field.grower?.name ?? "unknown",
                            ),
                        ),
                    );
                    const fileName = `performance_report_${year}__${uniqueGrowers.join("_")}.zip`;

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = fileName;
                    a.click();
                    window.URL.revokeObjectURL(url);
                },
            },
        );
    }, [downloadMutate, selectedFieldIds, selectedYear, tableData]);

    const headersById = useMemo(() => {
        return table
            .getHeaderGroups()
            .map((group) => group.headers.map((header) => header))
            .flat()
            .reduce(
                (acc, header) => {
                    acc[header.id] = header;
                    return acc;
                },
                {} as Record<string, Header<HarvestSummary, unknown>>,
            );
    }, [table]);

    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => 33,
        getScrollElement: () => ref.current,
        measureElement:
            typeof window !== "undefined" &&
            navigator.userAgent.indexOf("Firefox") === -1
                ? (element) => element?.getBoundingClientRect().height
                : undefined,
        overscan: 5,
    });

    return (
        <div className="h-full flex flex-col bg-white">
            <div className="flex flex-row gap-2 p-2 items-center justify-between">
                <div className="flex flex-row gap-2 items-center">
                    <Input
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        value={globalSearch}
                        isClearable
                        onClear={() => setGlobalSearch("")}
                        onChange={(e) =>
                            setGlobalSearch(String(e.target.value))
                        }
                        placeholder="Search..."
                        size="sm"
                    />
                    <Autocomplete
                        color={
                            headersById[
                                "service_provider"
                            ].column.getFilterValue()
                                ? "primary"
                                : "default"
                        }
                        aria-label="Filter"
                        className="w-full pointer-events-auto"
                        size="sm"
                        placeholder="Service Provider"
                        showScrollIndicators={false}
                        selectedKey={
                            headersById[
                                "service_provider"
                            ].column.getFilterValue() as string
                        }
                        onSelectionChange={(e) => {
                            headersById[
                                "service_provider"
                            ].column.setFilterValue(e);
                        }}
                    >
                        {filterOptions?.service_provider ?? (
                            <AutocompleteItem>N/A</AutocompleteItem>
                        )}
                    </Autocomplete>
                    <Autocomplete
                        color={
                            headersById["farm"].column.getFilterValue()
                                ? "primary"
                                : "default"
                        }
                        aria-label="Filter"
                        className="w-full pointer-events-auto"
                        size="sm"
                        placeholder="Grower"
                        showScrollIndicators={false}
                        selectedKey={
                            headersById[
                                "farm"
                            ].column.getFilterValue() as string
                        }
                        onSelectionChange={(e) => {
                            headersById["farm"].column.setFilterValue(e);
                        }}
                    >
                        {filterOptions?.farm ?? (
                            <AutocompleteItem>N/A</AutocompleteItem>
                        )}
                    </Autocomplete>
                    <Select
                        selectionMode="single"
                        size="sm"
                        aria-label="Year"
                        onSelectionChange={setSelectedYear}
                        selectedKeys={selectedYear}
                    >
                        <SelectItem key="2024">2024</SelectItem>
                        <SelectItem key="2025">2025</SelectItem>
                    </Select>
                </div>
                <div className="flex flex-row gap-2 items-center">
                    {selectedFieldIds?.length > 0 && (
                        <span>{selectedFieldIds.length} fields selected</span>
                    )}
                    <Button
                        startContent={<FontAwesomeIcon icon={faDownload} />}
                        onPress={handleExport}
                        isLoading={downloadMutate.isPending}
                        isDisabled={
                            downloadMutate.isPending ||
                            selectedFieldIds.length === 0
                        }
                        color="primary"
                        size="sm"
                    >
                        Export
                    </Button>
                </div>
            </div>
            <Divider />
            <div
                ref={ref}
                className="h-[100%] overflow-y-scroll  bg-white my-2"
            >
                <table style={{ display: "grid", height: height }}>
                    <thead
                        style={{
                            display: "grid",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                        className="bg-white"
                    >
                        {table.getHeaderGroups().map((group) => (
                            <tr className="border-b py-[0.25rem] border-gray-200">
                                {group.headers.map((header) => {
                                    return (
                                        <th
                                            aria-label={
                                                header.column.columnDef.header?.toString() ??
                                                "column header"
                                            }
                                            key={header.id}
                                            style={{
                                                width: header.column.getSize(),
                                            }}
                                            className="pointer-events-none"
                                        >
                                            <div
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    width: header.column.getSize(),
                                                }}
                                                className={classNames({
                                                    "hover:bg-gray-200 rounded-lg p-2 text-medium pointer-events-auto mb-1":
                                                        true,
                                                    "cursor-pointer":
                                                        header.column.getCanSort(),
                                                })}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {header.column.getCanSort() &&
                                                    header.column.getIsSorted() && (
                                                        <FontAwesomeIcon
                                                            className="ml-2 text-primary-500"
                                                            icon={
                                                                header.column.getIsSorted() ===
                                                                "asc"
                                                                    ? faArrowUpShortWide
                                                                    : faArrowDownShortWide
                                                            }
                                                            size="sm"
                                                        />
                                                    )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        style={{
                            display: "grid",
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            position: "relative",
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const row = rows[
                                virtualRow.index
                            ] as Row<HarvestSummary>;
                            return (
                                <tr
                                    data-index={virtualRow.index}
                                    ref={(node) =>
                                        rowVirtualizer.measureElement(node)
                                    }
                                    key={row.id}
                                    style={{
                                        display: "flex",
                                        position: "absolute",
                                        transform: `translateY(${virtualRow.start}px)`,
                                        width: "100%",
                                    }}
                                    className="border-b py-[0.25rem] border-gray-200"
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                style={{
                                                    display: "flex",
                                                    width: cell.column.getSize(),
                                                }}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
